import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import debounce from 'lodash/debounce';
import omit from 'lodash/omit';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { useIntl, FormattedMessage } from '../../util/reactIntl';
import {
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  isOriginInUse,
  getQueryParamNames,
} from '../../util/search';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  parse,
} from '../../util/urlHelpers';
import { createResourceLocatorString, pathByRouteName } from '../../util/routes';
import { propTypes } from '../../util/types';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors';
import {
  hasPermissionToViewData,
  isUserAuthorized,
  showCreateListingLinkForUser,
} from '../../util/userHelpers';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';

import {
  H3,
  H5,
  ModalInMobile,
  NamedLink,
  NamedRedirect,
  Page,
  IconSpinner,
} from '../../components';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';

import { setActiveListing, fetchFilterOptionCounts, loadData } from './SearchPage.duck';
import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  validFilterParams,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
  pickListingFieldFilters,
  omitLimitedListingFieldParams,
  getDatesAndSeatsMaybe,
  getSearchPageResourceLocatorStringParams,
  isPromotionsPath,
  getActiveCategoryName,
  getActiveBrandName,
  getActiveConnectivityName,
  getActiveCompatibilityName,
} from './SearchPage.shared';
import { normalizeIncomingSearchParams } from '../../util/searchParamMappers';
import {
  findSlugEntry,
  getCategoryEntryById,
  getRelatedLinks,
  getSeoByCategoryIds,
  getSlugSearchParams,
  getSubcategoryEntryByIds,
  ensureSlugEntries,
  getSlugEntriesCount,
  areSlugEntriesLoaded,
} from '../../util/categorySlugMap';

import FilterComponent from './FilterComponent';
import SearchMap from './SearchMap/SearchMap';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersSecondary from './SearchFiltersSecondary/SearchFiltersSecondary';
import SearchFiltersPrimary from './SearchFiltersPrimary/SearchFiltersPrimary';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';
import SearchBreadcrumb from './SearchBreadcrumb';

import css from './SearchPage.module.css';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout
const SEARCH_WITH_MAP_DEBOUNCE = 300; // Little bit of debounce before search is initiated.

// Primary filters have their content in dropdown-popup.
// With this offset we move the dropdown to the left a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;
const isDealsParamActive = value =>
  value === 'true' || value === true || value === '1' || value === 1;

const getSelectedSecondaryFiltersCount = (
  validQueryParams,
  filterConfigs,
  customSecondaryFilters
) => {
  const hasSecondaryFilters = !!(customSecondaryFilters && customSecondaryFilters.length > 0);
  const potentialSecondaryFilters = hasSecondaryFilters
    ? validFilterParams(validQueryParams, {
        ...filterConfigs,
        listingFieldsConfig: customSecondaryFilters,
      })
    : {};

  const relevantQueryParamNames = customSecondaryFilters.map(f => {
    return f.scope === 'public' ? `pub_${f.key}` : f.key;
  });
  const pickRelevant = name => relevantQueryParamNames.includes(name);
  const selectedSecondaryFilters = Object.keys(potentialSecondaryFilters).filter(pickRelevant);
  return selectedSecondaryFilters?.length;
};

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isSearchMapOpenOnMobile: false,
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
      isSecondaryFiltersOpen: false,
      slugDataReady: !props?.params?.slug || (areSlugEntriesLoaded() && getSlugEntriesCount() > 0),
    };

    this.onMapMoveEnd = debounce(this.onMapMoveEnd.bind(this), SEARCH_WITH_MAP_DEBOUNCE);
    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);

    // Filter functions
    this.applyFilters = this.applyFilters.bind(this);
    this.cancelFilters = this.cancelFilters.bind(this);
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);
    this.setCtaBannerRef = this.setCtaBannerRef.bind(this);
    this.updateCtaVisibility = this.updateCtaVisibility.bind(this);

    // Track last canonicalized search to prevent infinite loops
    this.lastCanonicalizedSearch = null;
    this.canonicalizeTimeout = null;

    this.lastFilterCountsKey = null;
    this.ctaBannerRef = null;
  }

  setCtaBannerRef(ref) {
    this.ctaBannerRef = ref;
  }

  updateCtaVisibility() {
    if (!this.ctaBannerRef) return;
    const banner = this.ctaBannerRef;
    const buttons = Array.from(banner.querySelectorAll(`.${css.categoryCtaButton}`));
    if (buttons.length === 0) return;

    buttons.forEach(btn => btn.classList.remove(css.categoryCtaButtonHidden));
    if (banner.scrollWidth > banner.clientWidth) {
      const lastButton = buttons[buttons.length - 1];
      if (lastButton) {
        lastButton.classList.add(css.categoryCtaButtonHidden);
      }
    }
  }

  // Callback to determine if new search is needed
  // when map is moved by user or viewport has changed
  onMapMoveEnd(viewportBoundsChanged, data) {
    const { viewportBounds, viewportCenter } = data;
    const { params: currentPathParams } = this.props;

    const routes = this.props.routeConfiguration;
    const searchPagePath = currentPathParams.slug
      ? pathByRouteName('SearchPageWithSlug', routes, { slug: currentPathParams.slug })
      : pathByRouteName('SearchPage', routes);
    const promotionsPagePath = pathByRouteName('PromotionsPage', routes);
    const currentPath =
      typeof window !== 'undefined' && window.location && window.location.pathname;

    // When using the ReusableMapContainer onMapMoveEnd can fire from other pages than SearchPage too
    const isSearchPage = currentPath === searchPagePath || currentPath === promotionsPagePath;

    // If mapSearch url param is given
    // or original location search is rendered once,
    // we start to react to "mapmoveend" events by generating new searches
    // (i.e. 'moveend' event in Mapbox and 'bounds_changed' in Google Maps)
    if (viewportBoundsChanged && isSearchPage) {
      const { history, location, config } = this.props;
      const { listingFields: listingFieldsConfig } = config?.listing || {};
      const { defaultFilters: defaultFiltersConfig } = config?.search || {};
      const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
      const listingCategories = config.categoryConfiguration.categories;
      const filterConfigs = {
        listingFieldsConfig,
        defaultFiltersConfig,
        listingCategories,
        activeListingTypes,
        currentPathParams,
      };

      // parse query parameters, including a custom attribute named category
      // when onMapMoveEnd is called, pagination needs to be reset.
      const { address, bounds, mapSearch, page, deals, ...restRaw } = parse(location.search, {
        latlng: ['origin'],
        latlngBounds: ['bounds'],
      });
      const rest = normalizeIncomingSearchParams(restRaw, filterConfigs);
      const dealsMaybe = isDealsParamActive(deals) ? { deals } : {};

      const originMaybe = isOriginInUse(this.props.config) ? { origin: viewportCenter } : {};
      const dropNonFilterParams = false;

      const searchParams = {
        address,
        ...originMaybe,
        bounds: viewportBounds,
        mapSearch: true,
        ...validFilterParams(rest, filterConfigs, dropNonFilterParams),
        ...dealsMaybe,
      };

      const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(routes, location);

      history.push(createResourceLocatorString(routeName, routes, pathParams, searchParams));
    }
  }

  componentDidMount() {
    setTimeout(() => this.ensureCanonicalQuery(), 0);

    this.processFilterCountFetch();
    const { params: currentPathParams = {}, config } = this.props;
    if (typeof window !== 'undefined') {
      if (currentPathParams?.slug && (!areSlugEntriesLoaded() || getSlugEntriesCount() === 0)) {
        this.setState({ slugDataReady: false });
      }
      ensureSlugEntries(config?.marketplaceRootURL)
        .then(() =>
          this.setState({ slugDataReady: getSlugEntriesCount() > 0 || !currentPathParams?.slug })
        )
        .catch(() =>
          this.setState({ slugDataReady: getSlugEntriesCount() > 0 || !currentPathParams?.slug })
        );
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.updateCtaVisibility);
    }
    setTimeout(() => this.updateCtaVisibility(), 0);
  }

  componentWillUnmount() {
    // Cleanup timeout to prevent memory leaks
    if (this.canonicalizeTimeout) {
      clearTimeout(this.canonicalizeTimeout);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.updateCtaVisibility);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const currentSearch = this.props.location.search;
    const prevSearch = prevProps.location.search;
    const currentSlug = this.props.params?.slug;
    const prevSlug = prevProps.params?.slug;

    // Only canonicalize if search actually changed and it's not the result of our own canonicalization
    // Add debounce to prevent rapid successive calls
    if (prevSearch !== currentSearch && this.lastCanonicalizedSearch !== currentSearch) {
      // Debounce canonicalization to avoid excessive calls during rapid filter changes
      if (this.canonicalizeTimeout) {
        clearTimeout(this.canonicalizeTimeout);
      }
      this.canonicalizeTimeout = setTimeout(() => {
        this.ensureCanonicalQuery();
      }, 100);
    }

    if (currentSlug && currentSlug !== prevSlug && typeof window !== 'undefined') {
      const { config } = this.props;
      if (!areSlugEntriesLoaded() || getSlugEntriesCount() === 0) {
        this.setState({ slugDataReady: false });
      }
      ensureSlugEntries(config?.marketplaceRootURL)
        .then(() => this.setState({ slugDataReady: getSlugEntriesCount() > 0 }))
        .catch(() => this.setState({ slugDataReady: getSlugEntriesCount() > 0 }));
    }

    if (
      this.props.params?.slug &&
      !prevState.slugDataReady &&
      this.state.slugDataReady &&
      typeof this.props.onLoadData === 'function'
    ) {
      this.props.onLoadData(this.props.params, this.props.location.search, this.props.config);
    }

    if (!prevProps.searchInProgress && this.props.searchInProgress) {
      this.lastFilterCountsKey = null;
    }

    this.processFilterCountFetch();
    if (
      prevProps.location.search !== this.props.location.search ||
      prevProps.listings !== this.props.listings
    ) {
      setTimeout(() => this.updateCtaVisibility(), 0);
    }
  }

  ensureCanonicalQuery() {
    const {
      history,
      routeConfiguration,
      location,
      config,
      params: currentPathParams = {},
    } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};
    const activeListingTypes = config?.listing?.listingTypes.map(c => c.listingType);
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    };

    const { mapSearch, page, ...raw } = parse(location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });
    const normalized = normalizeIncomingSearchParams(raw, filterConfigs);
    const validated = validFilterParams(normalized, filterConfigs, false);
    const canonicalFrom = Object.keys(validated).length > 0 ? validated : normalized;

    // Include page parameter in canonical URL if it exists
    let canonicalParams = page ? { ...canonicalFrom, page } : canonicalFrom;

    let { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );
    const isPromotionsRoute = routeName === 'PromotionsPage';

    const categoryId = canonicalFrom.pub_categoryLevel1 || canonicalFrom.category;
    const subcategoryId = canonicalFrom.pub_categoryLevel2 || canonicalFrom.subcategory;
    if (!currentPathParams.slug && categoryId && !isPromotionsRoute) {
      const subcategoryEntry = subcategoryId
        ? getSubcategoryEntryByIds(categoryId, subcategoryId)
        : null;
      const categoryEntry = getCategoryEntryById(categoryId);
      const targetSlug = subcategoryEntry?.slug || categoryEntry?.slug;
      if (targetSlug) {
        routeName = 'SearchPageWithSlug';
        pathParams = { slug: targetSlug };
        canonicalParams = omit(canonicalParams, [
          'category',
          'subcategory',
          'pub_categoryLevel1',
          'pub_categoryLevel2',
        ]);
      }
    }

    const canonical = createResourceLocatorString(
      routeName,
      routeConfiguration,
      pathParams,
      canonicalParams
    );
    const current = `${location.pathname}${location.search}`;

    // Prevent infinite loops: only replace if search differs AND we haven't just canonicalized this exact search
    const currentSearch = new URL('http://x' + current).search;
    const canonicalSearch = new URL('http://x' + canonical).search;

    if (canonicalSearch !== currentSearch && this.lastCanonicalizedSearch !== canonicalSearch) {
      this.lastCanonicalizedSearch = canonicalSearch;
      history.replace(canonical);
    }
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  // Apply the filters by redirecting to SearchPage with new filters.
  applyFilters() {
    const { history, routeConfiguration, config, params: currentPathParams, location } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    };

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const searchParams = { ...urlQueryParams, ...this.state.currentQueryParams };
    const search = cleanSearchFromConflictingParams(searchParams, filterConfigs, sortConfig);
    const rawQueryParams = parse(location.search);
    const dealsMaybe = isDealsParamActive(rawQueryParams.deals)
      ? { deals: rawQueryParams.deals }
      : {};

    let { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );
    const shouldDropSlug =
      currentPathParams?.slug &&
      (search.pub_categoryLevel1 ||
        search.pub_categoryLevel2 ||
        search.pub_categoryLevel3 ||
        search.category ||
        search.subcategory ||
        search.subsubcategory);
    if (shouldDropSlug) {
      routeName = 'SearchPage';
      pathParams = {};
    }

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, {
        ...search,
        ...dealsMaybe,
      })
    );
  }

  // Close the filters by clicking cancel, revert to the initial params
  cancelFilters() {
    this.setState({ currentQueryParams: {} });
  }

  // Reset all filter query parameters
  resetAll(e) {
    const { history, routeConfiguration, config, location } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const filterQueryParamNames = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);

    // Reset state
    this.setState({ currentQueryParams: {} });

    // Reset routing params - remove all filter params and also deals
    const queryParams = omit(urlQueryParams, [...filterQueryParamNames, 'deals']);

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, queryParams)
    );
  }

  getHandleChangedValueFn(useHistoryPush) {
    const {
      history,
      routeConfiguration,
      config,
      location,
      params: currentPathParams = {},
    } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    };

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    return updatedURLParams => {
      const rawQueryParams = parse(location.search);
      const hasKeywordChange = Object.prototype.hasOwnProperty.call(updatedURLParams, 'keywords');
      const dealsMaybe =
        isDealsParamActive(rawQueryParams.deals) && !hasKeywordChange
          ? { deals: rawQueryParams.deals }
          : {};
      const updater = prevState => {
        const { address, bounds, keywords } = urlQueryParams;
        const mergedQueryParams = { ...urlQueryParams, ...prevState.currentQueryParams };

        // Address and bounds are handled outside of MainPanel.
        // I.e. TopbarSearchForm && search by moving the map.
        // We should always trust urlQueryParams with those.
        // The same applies to keywords, if the main search type is keyword search.
        const keywordsMaybe = isMainSearchTypeKeywords(config) ? { keywords } : {};

        const datesAndSeatsMaybe = getDatesAndSeatsMaybe(mergedQueryParams, updatedURLParams);

        return {
          currentQueryParams: omitLimitedListingFieldParams(
            {
              ...mergedQueryParams,
              ...updatedURLParams,
              ...keywordsMaybe,
              ...datesAndSeatsMaybe,
              address,
              bounds,
            },
            filterConfigs
          ),
        };
      };

      const callback = () => {
        if (useHistoryPush) {
          const searchParams = this.state.currentQueryParams;
          const search = cleanSearchFromConflictingParams(searchParams, filterConfigs, sortConfig);

          const hasCategoryFilterChange = [
            'category',
            'subcategory',
            'subsubcategory',
            'pub_categoryLevel1',
            'pub_categoryLevel2',
            'pub_categoryLevel3',
          ].some(key => Object.prototype.hasOwnProperty.call(updatedURLParams, key));
          let { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
            routeConfiguration,
            location
          );
          const isPromotionsRoute = routeName === 'PromotionsPage';
          let nextSearch = { ...search, ...dealsMaybe };
          if (hasCategoryFilterChange && !isPromotionsRoute) {
            const categoryId = search.pub_categoryLevel1 || search.category;
            const subcategoryId = search.pub_categoryLevel2 || search.subcategory;
            const slugEntry = subcategoryId
              ? getSubcategoryEntryByIds(categoryId, subcategoryId)
              : getCategoryEntryById(categoryId);
            if (slugEntry?.slug) {
              routeName = 'SearchPageWithSlug';
              pathParams = { slug: slugEntry.slug };
              nextSearch = omit(search, [
                'category',
                'subcategory',
                'subsubcategory',
                'pub_categoryLevel1',
                'pub_categoryLevel2',
                'pub_categoryLevel3',
              ]);
            } else if (currentPathParams?.slug) {
              routeName = 'SearchPage';
              pathParams = {};
            }
          }

          history.push(
            createResourceLocatorString(routeName, routeConfiguration, pathParams, nextSearch)
          );
        }
      };

      this.setState(updater, callback);
    };
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration, location } = this.props;
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    const queryParams = values
      ? { ...urlQueryParams, [urlParam]: values }
      : omit(urlQueryParams, urlParam);

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, queryParams)
    );
  }

  maybeRequestFilterCounts(validQueryParams, availableFilters) {
    if (!Array.isArray(availableFilters) || availableFilters.length === 0) {
      this.lastFilterCountsKey = null;
      return;
    }

    const paramsForFetch = { ...validQueryParams };
    delete paramsForFetch.page;
    delete paramsForFetch.perPage;

    // Include deals parameter from URL if present (it's not a valid filter param)
    if (this.props.location?.search) {
      const rawQueryParams = parse(this.props.location.search);
      const isPromotionsPage = isPromotionsPath(this.props.location?.pathname);
      if (
        rawQueryParams.deals === 'true' ||
        rawQueryParams.deals === true ||
        rawQueryParams.deals === '1' ||
        rawQueryParams.deals === 1 ||
        isPromotionsPage
      ) {
        paramsForFetch.deals = rawQueryParams.deals || 'true';
      }
    }

    const paramsKey = JSON.stringify(paramsForFetch);
    const filtersKey = availableFilters
      .map(f => f && f.key)
      .filter(Boolean)
      .sort()
      .join('|');
    const combinedKey = `${paramsKey}::${filtersKey}`;

    if (this.lastFilterCountsKey === combinedKey) {
      return;
    }

    this.lastFilterCountsKey = combinedKey;

    if (typeof this.props.onFetchFilterCounts === 'function') {
      const maybePromise = this.props.onFetchFilterCounts(paramsForFetch, availableFilters);
      if (maybePromise && typeof maybePromise.then === 'function') {
        maybePromise.catch(() => {
          this.lastFilterCountsKey = null;
        });
      }
    }
  }

  processFilterCountFetch() {
    const context = this.latestFilterFetchContext || {};
    const {
      listingsAreLoaded,
      searchParamsAreInSync,
      validQueryParams,
      availableFilters,
    } = context;

    if (listingsAreLoaded && searchParamsAreInSync) {
      this.maybeRequestFilterCounts(validQueryParams, availableFilters);
    } else if (!listingsAreLoaded) {
      this.lastFilterCountsKey = null;
    }
  }

  render() {
    const {
      intl,
      listings = [],
      location,
      history,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams = {},
      activeListingId,
      onActivateListing,
      routeConfiguration,
      config,
      params: currentPathParams = {},
      currentUser,
    } = this.props;

    // If the search page variant is of type /s/:listingType, this defines the :listingType
    // path parameter used to filter the whole page.
    //
    // On a default search page (/s), this constant does not have a value, even when a
    // query parameter ?pub_listingType=[queryParamListingType] is used.
    const slugSearchParams = getSlugSearchParams(currentPathParams, config);
    const listingTypePathParam = slugSearchParams.pub_listingType;

    const { listingFields } = config?.listing || {};
    const { defaultFilters: defaultFiltersRaw, sortConfig } = config?.search || {};

    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const defaultFiltersConfig = listingTypePathParam
      ? defaultFiltersRaw.filter(f => f.key !== 'listingType')
      : defaultFiltersRaw;

    const marketplaceCurrency = config.currency;
    const categoryConfiguration = config.categoryConfiguration;
    const listingCategories = categoryConfiguration.categories;
    const listingFieldsConfig = pickListingFieldFilters({
      listingFields,
      locationSearch: location.search,
      categoryConfiguration,
      activeListingTypes,
      currentPathParams,
    });
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    };

    // Page transition might initially use values from previous search
    // urlQueryParams doesn't contain page specific url params
    // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
    const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
      location.search,
      searchParams,
      filterConfigs,
      sortConfig,
      isOriginInUse(config),
      currentPathParams,
      config
    );

    const validQueryParams = urlQueryParams;
    const slugFromPath = location?.pathname?.startsWith('/products/')
      ? location.pathname.replace('/products/', '').split('/')[0]
      : null;
    const breadcrumbParams =
      currentPathParams?.slug || slugFromPath
        ? {
            ...validQueryParams,
            ...getSlugSearchParams({ slug: currentPathParams?.slug || slugFromPath }, config),
          }
        : validQueryParams;
    const isWindowDefined = typeof window !== 'undefined';
    const isMobileLayout = isWindowDefined && window.innerWidth < MODAL_BREAKPOINT;
    const shouldShowSearchMap =
      !isMobileLayout || (isMobileLayout && this.state.isSearchMapOpenOnMobile);

    const isKeywordSearch = isMainSearchTypeKeywords(config);
    const hideCategoryFilter = !!currentPathParams?.slug;
    const builtInPrimaryFilters = defaultFiltersConfig
      .filter(f => ['categoryLevel', 'listingType'].includes(f.key))
      .filter(f => !(hideCategoryFilter && f.key === 'categoryLevel'));
    const builtInFilters = isKeywordSearch
      ? defaultFiltersConfig.filter(
          f => !['keywords', 'categoryLevel', 'listingType'].includes(f.key)
        )
      : defaultFiltersConfig.filter(f => !['categoryLevel', 'listingType'].includes(f.key));
    const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
      listingFieldsConfig,
      activeListingTypes
    );
    const availablePrimaryFilters = [
      ...builtInPrimaryFilters,
      ...customPrimaryFilters,
      ...builtInFilters,
    ];
    const availableFilters = [
      ...builtInPrimaryFilters,
      ...customPrimaryFilters,
      ...builtInFilters,
      ...customSecondaryFilters,
    ];

    const hasSecondaryFilters = !!(customSecondaryFilters && customSecondaryFilters.length > 0);

    // Selected aka active filters
    const selectedFilters = validQueryParams;
    const keysOfSelectedFilters = Object.keys(selectedFilters);
    // Exclude sort parameter and keywords from filter count
    const excludedParams = ['keywords', sortConfig.queryParamName];
    const selectedFiltersCountForMobile = isKeywordSearch
      ? keysOfSelectedFilters.filter(f => !excludedParams.includes(f)).length
      : keysOfSelectedFilters.filter(f => f !== sortConfig.queryParamName).length;
    const isValidDatesFilter =
      searchParamsInURL.dates == null ||
      (searchParamsInURL.dates != null && searchParamsInURL.dates === selectedFilters.dates);

    const selectedSecondaryFiltersCount = getSelectedSecondaryFiltersCount(
      validQueryParams,
      filterConfigs,
      customSecondaryFilters
    );

    const isSecondaryFiltersOpen = !!hasSecondaryFilters && this.state.isSecondaryFiltersOpen;
    const propsForSecondaryFiltersToggle = hasSecondaryFilters
      ? {
          isSecondaryFiltersOpen: this.state.isSecondaryFiltersOpen,
          toggleSecondaryFiltersOpen: isOpen => {
            this.setState({ isSecondaryFiltersOpen: isOpen, currentQueryParams: {} });
          },
          selectedSecondaryFiltersCount,
        }
      : {};

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const totalItems =
      searchParamsAreInSync && hasPaginationInfo
        ? pagination.totalItems
        : pagination?.paginationUnsupported
        ? listings.length
        : 0;
    const listingsAreLoaded =
      !searchInProgress &&
      searchParamsAreInSync &&
      !!(hasPaginationInfo || pagination?.paginationUnsupported);

    const filterOptionCounts = searchParamsAreInSync ? this.props.filterOptionCounts || {} : {};

    this.latestFilterFetchContext = {
      listingsAreLoaded,
      searchParamsAreInSync,
      validQueryParams,
      availableFilters,
    };

    const conflictingFilterActive = isAnyFilterActive(
      sortConfig.conflictingFilters,
      validQueryParams,
      filterConfigs
    );

    const showCreateListingsLink = false;
    const sortParamName = sortConfig?.queryParamName;
    const rawQueryParams = parse(location.search);
    const isPromotionsPage = isPromotionsPath(location.pathname);
    const isDealsActive =
      rawQueryParams.deals === 'true' ||
      rawQueryParams.deals === true ||
      rawQueryParams.deals === '1' ||
      rawQueryParams.deals === 1 ||
      isPromotionsPage;
    const hasExplicitSort = sortParamName
      ? Object.prototype.hasOwnProperty.call(rawQueryParams, sortParamName)
      : false;
    const nonFilterKeys = [sortParamName, 'perPage'].filter(Boolean);
    const hasFilterParams = Object.keys(searchParamsInURL || {}).some(
      key => !nonFilterKeys.includes(key)
    );
    const shouldRandomizeResults =
      !listingTypePathParam && !isDealsActive && !hasExplicitSort && !hasFilterParams;

    const sortValueForUi = shouldRandomizeResults
      ? null
      : validQueryParams[sortConfig.queryParamName];
    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          sort={sortValueForUi}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          isRandomSortActive={shouldRandomizeResults}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
        />
      ) : null;
    };
    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
        showCreateListingsLink={showCreateListingsLink}
      />
    );

    // Get heading based on active filters
    const brandName = getActiveBrandName(validQueryParams, listingFieldsConfig);
    const connectivityName = getActiveConnectivityName(validQueryParams, listingFieldsConfig);
    const compatibilityName = getActiveCompatibilityName(validQueryParams, listingFieldsConfig);
    const categoryName = getActiveCategoryName(validQueryParams, listingCategories);

    const noFiltersApplied = !hasFilterParams && !isDealsActive && !listingTypePathParam;
    const slugParam = currentPathParams?.slug ?? slugFromPath;
    const slugEntry = findSlugEntry(slugParam ?? '');
    const isRootPath = !slugParam;
    const shouldUseRootSlugEntry = isRootPath && noFiltersApplied;
    const effectiveSlugEntry = isRootPath ? (shouldUseRootSlugEntry ? slugEntry : null) : slugEntry;
    const resultsMessage =
      noFiltersApplied || shouldUseRootSlugEntry ? (
        <FormattedMessage id="SearchPage.moreThanThousandProducts" />
      ) : null;
    const resultsCountForUi = shouldUseRootSlugEntry ? null : totalItems;

    // Count how many specific filters are active (brand, connectivity, compatibility)
    const activeSpecificFiltersCount = [brandName, connectivityName, compatibilityName].filter(
      Boolean
    ).length;

    // Determine the heading based on combination of filters and deals
    let categoryHeading;

    if (isDealsActive) {
      // Deals mode is active
      if (categoryName && brandName) {
        // Category + brand + deals: "comme aujourd'hui + en promotion"
        categoryHeading = `${categoryName} ${brandName} en promotion`;
      } else if (categoryName && connectivityName) {
        // Category + connectivity + deals
        categoryHeading = `${categoryName} ${connectivityName} en promotion`;
      } else if (categoryName && compatibilityName) {
        // Category + compatibility + deals
        categoryHeading = `${categoryName} compatibles ${compatibilityName} en promotion`;
      } else if (categoryName) {
        // Category + deals
        categoryHeading = `${categoryName} en promotion`;
      } else if (brandName) {
        // Brand + deals: "Nos bons plans Marque"
        categoryHeading = `Nos bons plans ${brandName}`;
      } else if (connectivityName) {
        // Connectivity + deals: "Nos bons plans connectivité"
        categoryHeading = `Nos bons plans ${connectivityName}`;
      } else if (compatibilityName) {
        // Compatibility + deals
        categoryHeading = `Nos bons plans compatibles ${compatibilityName}`;
      } else {
        // Deals only: "Nos bons plans & Promotions"
        categoryHeading = 'Nos bons plans & Promotions';
      }
    } else {
      // Normal mode (no deals)
      if (categoryName) {
        // Category is active
        if (activeSpecificFiltersCount === 0) {
          // Only category
          categoryHeading = categoryName;
        } else if (activeSpecificFiltersCount === 1) {
          // Category + one specific filter
          if (brandName) {
            categoryHeading = `${categoryName} ${brandName}`;
          } else if (connectivityName) {
            categoryHeading = `${categoryName} ${connectivityName}`;
          } else if (compatibilityName) {
            categoryHeading = `${categoryName} compatibles ${compatibilityName}`;
          }
        } else {
          // Category + multiple specific filters
          categoryHeading = categoryName;
        }
      } else {
        // No category filter
        if (activeSpecificFiltersCount === 1) {
          // Only one specific filter
          if (brandName) {
            categoryHeading = `Nos produits ${brandName}`;
          } else if (connectivityName) {
            categoryHeading = `Nos produits ${connectivityName}`;
          } else if (compatibilityName) {
            categoryHeading = `Nos produits compatibles ${compatibilityName}`;
          }
        } else {
          // Multiple specific filters or no filters at all
          categoryHeading = intl.formatMessage({ id: 'SearchPage.defaultCatalogueHeading' });
        }
      }
    }

    const categoryIdForSEO =
      effectiveSlugEntry?.categoryId ||
      searchParamsInURL.pub_categoryLevel1 ||
      searchParamsInURL.category;
    const subcategoryIdForSEO =
      effectiveSlugEntry?.subcategoryId ||
      searchParamsInURL.pub_categoryLevel2 ||
      searchParamsInURL.subcategory;
    const categorySeoData =
      effectiveSlugEntry?.seo ||
      (categoryIdForSEO ? getSeoByCategoryIds(categoryIdForSEO, subcategoryIdForSEO) : null);
    const categoryDescription =
      effectiveSlugEntry && categorySeoData?.metaDescription
        ? categorySeoData.metaDescription
        : null;
    const categoryIntro =
      effectiveSlugEntry && categorySeoData?.intro ? categorySeoData.intro : null;
    const faqItems = Array.isArray(categorySeoData?.faq) ? categorySeoData.faq : [];
    if (effectiveSlugEntry && categorySeoData?.h1) {
      categoryHeading = categorySeoData.h1;
    }
    const slugPageLoading = currentPathParams?.slug && !this.state.slugDataReady;
    const headingToRender = slugPageLoading ? null : categoryHeading;
    const introToRender = slugPageLoading ? null : categoryIntro;

    const relatedLinks = effectiveSlugEntry ? getRelatedLinks(effectiveSlugEntry) : [];
    const guideLink = effectiveSlugEntry?.guideLink || null;
    const comparatorCta = effectiveSlugEntry?.comparatorCta || null;
    const mergedCtaItems = relatedLinks;
    const showCtaBanner = mergedCtaItems.length > 0;
    const mobileGuideComparatorCtas =
      guideLink?.url || comparatorCta?.url ? (
        <div className={css.mobileCtaRow}>
          <div className={css.seoCategoryHeadingCtasPair}>
            {guideLink?.url ? (
              <a
                className={`${css.categoryGuideButton} ${css.categoryCtaFixedButton}`}
                href={guideLink.url}
                onClick={e => {
                  if (guideLink.url.startsWith('/')) {
                    e.preventDefault();
                    history.push(guideLink.url);
                  }
                }}
              >
                <span className={css.categoryCtaIcon} aria-hidden="true">
                  <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" fill="none">
                    <g fill="currentColor">
                      <path d="M12.75 1a.75.75 0 000 1.5h.69l-1.97 1.97a.75.75 0 001.06 1.06l1.97-1.97v.69a.75.75 0 001.5 0v-2.5a.75.75 0 00-.75-.75h-2.5z"></path>
                      <path
                        fillRule="evenodd"
                        d="M1.25 2C.56 2 0 2.56 0 3.25v8.5C0 12.44.56 13 1.25 13H5c.896 0 1.475.205 1.809.448.317.23.441.51.441.802a.751.751 0 101.5 0c0-.292.124-.572.441-.802.334-.243.913-.448 1.809-.448h3.75c.69 0 1.25-.56 1.25-1.25v-4.5a.75.75 0 00-1.5 0v4.25H11c-.878 0-1.64.158-2.25.467v-6.55c0-.788.376-1.42 1.12-1.722a.75.75 0 00-.561-1.39 3.27 3.27 0 00-1.31.941A3.13 3.13 0 007.773 3C7.106 2.354 6.154 2 5 2H1.25zm6 3.417c0-.553-.187-1.015-.522-1.34C6.394 3.753 5.846 3.5 5 3.5H1.5v8H5c.878 0 1.64.158 2.25.467v-6.55z"
                        clipRule="evenodd"
                      ></path>
                    </g>
                  </svg>
                </span>
                {guideLink.label || 'Voir le guide'}
              </a>
            ) : null}
            {comparatorCta?.url ? (
              <a
                className={`${css.categoryGuideButton} ${css.categoryCtaFixedButton}`}
                href={comparatorCta.url}
                onClick={e => {
                  if (comparatorCta.url.startsWith('/')) {
                    e.preventDefault();
                    history.push(comparatorCta.url);
                  }
                }}
              >
                <span className={css.categoryCtaIcon} aria-hidden="true">
                  <svg fill="currentColor" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                    <g>
                      <path d="M10.08,7l1,1,3.44-3.45L11,1,10,2l1.8,1.8H2v1.4h9.82ZM5.86,9l-1-1L1.42,11.5,4.91,15l1-1L4.1,12.2H14V10.8H4.1Z"></path>
                    </g>
                  </svg>
                </span>
                {comparatorCta.label || 'Comparer'}
              </a>
            ) : null}
          </div>
        </div>
      ) : null;
    const mobileRelatedLinksCta = showCtaBanner ? (
      <div className={css.categoryCtaBanner}>
        <div className={css.categoryCtaContent}>
          <span className={css.categoryCtaLabel}>Voir aussi :</span>
          <div className={css.categoryCtaInline}>
            {mergedCtaItems.map(item => (
              <NamedLink
                key={item.slug}
                name="SearchPageWithSlug"
                params={{ slug: item.slug }}
                className={`${css.categoryCtaButton} ${css.categoryCtaInlineButton}`}
              >
                {item.label || item.slug}
              </NamedLink>
            ))}
          </div>
        </div>
      </div>
    ) : null;

    const { bounds, origin } = searchParamsInURL || {};
    const { title, description, schema } = createSearchResultSchema(
      listings,
      {
        ...(searchParamsInURL || {}),
        ...(isDealsActive ? { deals: 'true' } : {}),
      },
      intl,
      routeConfiguration,
      config
    );
    const resolvedTitle = categorySeoData?.title || title;
    const resolvedDescription = categorySeoData?.metaDescription || description;
    const faqSchema = faqItems.length
      ? {
          '@type': 'FAQPage',
          mainEntity: faqItems.map(item => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.answer,
            },
          })),
        }
      : null;
    const baseSchema = schema ? (Array.isArray(schema) ? schema : [schema]) : [];
    const pageSchema = faqSchema ? [...baseSchema, faqSchema] : schema;

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={resolvedDescription}
        title={resolvedTitle}
        schema={pageSchema}
      >
        <TopbarContainer rootClassName={topbarClasses} currentSearchParams={validQueryParams} />
        <div className={css.container}>
          <div className={css.searchResultContainer}>
            {!isRootPath &&
            (hasFilterParams ||
              isDealsActive ||
              listingTypePathParam ||
              currentPathParams?.slug ||
              slugFromPath) ? (
              <SearchBreadcrumb queryParams={breadcrumbParams} onResetAll={this.resetAll} />
            ) : null}
            {/* Mobile category heading */}
            <div className={css.mobileHeadingWrapper}>
              {slugPageLoading ? (
                <div className={css.categoryHeadingSpinner}>
                  <IconSpinner />
                </div>
              ) : (
                <>
                  <h1 className={css.mobileHeading}>{headingToRender}</h1>
                  {introToRender ? (
                    <p className={css.mobileCategoryIntro}>{introToRender}</p>
                  ) : null}
                </>
              )}
              {mobileGuideComparatorCtas || mobileRelatedLinksCta ? (
                <div className={css.mobileCtaWrapper}>
                  {mobileGuideComparatorCtas}
                  {mobileRelatedLinksCta}
                </div>
              ) : null}
            </div>
            <SearchFiltersMobile
              className={css.searchFiltersMobileMap}
              urlQueryParams={validQueryParams}
              sortByComponent={sortBy('mobile')}
              listingsAreLoaded={listingsAreLoaded}
              resultsCount={resultsCountForUi}
              resultsMessage={resultsMessage}
              searchInProgress={searchInProgress}
              searchListingsError={searchListingsError}
              showAsModalMaxWidth={MODAL_BREAKPOINT}
              onMapIconClick={() => this.setState({ isSearchMapOpenOnMobile: true })}
              onManageDisableScrolling={onManageDisableScrolling}
              onOpenModal={this.onOpenMobileModal}
              onCloseModal={this.onCloseMobileModal}
              resetAll={this.resetAll}
              selectedFiltersCount={selectedFiltersCountForMobile}
              noResultsInfo={noResultsInfo}
              location={location}
              isMapVariant
            >
              {availableFilters.map(filterConfig => {
                const key = `SearchFiltersMobile.${filterConfig.scope || 'built-in'}.${
                  filterConfig.key
                }`;
                return (
                  <FilterComponent
                    key={key}
                    idPrefix="SearchFiltersMobile"
                    config={filterConfig}
                    listingCategories={listingCategories}
                    marketplaceCurrency={marketplaceCurrency}
                    urlQueryParams={validQueryParams}
                    filterOptionCounts={filterOptionCounts}
                    initialValues={initialValues(this.props, this.state.currentQueryParams)}
                    getHandleChangedValueFn={this.getHandleChangedValueFn}
                    intl={intl}
                    showCounts={false}
                    liveEdit
                    showAsPopup={false}
                  />
                );
              })}
            </SearchFiltersMobile>
            {slugPageLoading ? (
              <div className={css.categoryHeadingSpinner}>
                <IconSpinner />
              </div>
            ) : null}
            <MainPanelHeader
              className={css.mainPanelMapVariant}
              sortByComponent={sortBy('desktop')}
              isSortByActive={sortConfig.active}
              listingsAreLoaded={listingsAreLoaded}
              resultsCount={resultsCountForUi}
              resultsMessage={resultsMessage}
              searchInProgress={searchInProgress}
              searchListingsError={searchListingsError}
              noResultsInfo={noResultsInfo}
              categoryHeading={headingToRender}
              categoryIntro={introToRender}
              headingCtas={
                guideLink?.url || comparatorCta?.url ? (
                  <div className={css.seoCategoryHeadingCtasPair}>
                    {guideLink?.url ? (
                      <a
                        className={`${css.categoryGuideButton} ${css.categoryCtaFixedButton}`}
                        href={guideLink.url}
                        onClick={e => {
                          if (guideLink.url.startsWith('/')) {
                            e.preventDefault();
                            history.push(guideLink.url);
                          }
                        }}
                      >
                        <span className={css.categoryCtaIcon} aria-hidden="true">
                          <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" fill="none">
                            <g fill="currentColor">
                              <path d="M12.75 1a.75.75 0 000 1.5h.69l-1.97 1.97a.75.75 0 001.06 1.06l1.97-1.97v.69a.75.75 0 001.5 0v-2.5a.75.75 0 00-.75-.75h-2.5z"></path>
                              <path
                                fillRule="evenodd"
                                d="M1.25 2C.56 2 0 2.56 0 3.25v8.5C0 12.44.56 13 1.25 13H5c.896 0 1.475.205 1.809.448.317.23.441.51.441.802a.751.751 0 101.5 0c0-.292.124-.572.441-.802.334-.243.913-.448 1.809-.448h3.75c.69 0 1.25-.56 1.25-1.25v-4.5a.75.75 0 00-1.5 0v4.25H11c-.878 0-1.64.158-2.25.467v-6.55c0-.788.376-1.42 1.12-1.722a.75.75 0 00-.561-1.39 3.27 3.27 0 00-1.31.941A3.13 3.13 0 007.773 3C7.106 2.354 6.154 2 5 2H1.25zm6 3.417c0-.553-.187-1.015-.522-1.34C6.394 3.753 5.846 3.5 5 3.5H1.5v8H5c.878 0 1.64.158 2.25.467v-6.55z"
                                clipRule="evenodd"
                              ></path>
                            </g>
                          </svg>
                        </span>
                        {guideLink.label || 'Voir le guide'}
                      </a>
                    ) : null}
                    {comparatorCta?.url ? (
                      <a
                        className={`${css.categoryGuideButton} ${css.categoryCtaFixedButton}`}
                        href={comparatorCta.url}
                        onClick={e => {
                          if (comparatorCta.url.startsWith('/')) {
                            e.preventDefault();
                            history.push(comparatorCta.url);
                          }
                        }}
                      >
                        <span className={css.categoryCtaIcon} aria-hidden="true">
                          <svg
                            fill="currentColor"
                            viewBox="0 0 16 16"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <g>
                              <path d="M10.08,7l1,1,3.44-3.45L11,1,10,2l1.8,1.8H2v1.4h9.82ZM5.86,9l-1-1L1.42,11.5,4.91,15l1-1L4.1,12.2H14V10.8H4.1Z"></path>
                            </g>
                          </svg>
                        </span>
                        {comparatorCta.label || 'Comparer'}
                      </a>
                    ) : null}
                  </div>
                ) : null
              }
              ctaContent={
                showCtaBanner ? (
                  <div className={css.categoryCtaBanner} ref={this.setCtaBannerRef}>
                    <div className={css.categoryCtaContent}>
                      <span className={css.categoryCtaLabel}>Voir aussi :</span>
                      <div className={css.categoryCtaInline}>
                        {mergedCtaItems.map(item => (
                          <NamedLink
                            key={item.slug}
                            name="SearchPageWithSlug"
                            params={{ slug: item.slug }}
                            className={`${css.categoryCtaButton} ${css.categoryCtaInlineButton}`}
                          >
                            {item.label || item.slug}
                          </NamedLink>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : null
              }
            >
              <SearchFiltersPrimary {...propsForSecondaryFiltersToggle}>
                {availablePrimaryFilters.map(filterConfig => {
                  const key = `SearchFiltersPrimary.${filterConfig.scope || 'built-in'}.${
                    filterConfig.key
                  }`;
                  return (
                    <FilterComponent
                      key={key}
                      idPrefix="SearchFiltersPrimary"
                      config={filterConfig}
                      listingCategories={listingCategories}
                      marketplaceCurrency={marketplaceCurrency}
                      urlQueryParams={validQueryParams}
                      filterOptionCounts={filterOptionCounts}
                      initialValues={initialValues(this.props, this.state.currentQueryParams)}
                      getHandleChangedValueFn={this.getHandleChangedValueFn}
                      intl={intl}
                      showCounts={false}
                      showAsPopup
                      contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
                    />
                  );
                })}
              </SearchFiltersPrimary>
            </MainPanelHeader>
            {isSecondaryFiltersOpen ? (
              <div className={classNames(css.searchFiltersPanel)}>
                <SearchFiltersSecondary
                  urlQueryParams={validQueryParams}
                  listingsAreLoaded={listingsAreLoaded}
                  applyFilters={this.applyFilters}
                  cancelFilters={this.cancelFilters}
                  resetAll={this.resetAll}
                  onClosePanel={() => this.setState({ isSecondaryFiltersOpen: false })}
                >
                  {customSecondaryFilters.map(filterConfig => {
                    const key = `SearchFiltersSecondary.${filterConfig.scope || 'built-in'}.${
                      filterConfig.key
                    }`;
                    return (
                      <FilterComponent
                        key={key}
                        idPrefix="SearchFiltersSecondary"
                        config={filterConfig}
                        listingCategories={listingCategories}
                        marketplaceCurrency={marketplaceCurrency}
                        urlQueryParams={validQueryParams}
                        filterOptionCounts={filterOptionCounts}
                        initialValues={initialValues(this.props, this.state.currentQueryParams)}
                        getHandleChangedValueFn={this.getHandleChangedValueFn}
                        intl={intl}
                        showCounts={false}
                        showAsPopup={false}
                      />
                    );
                  })}
                </SearchFiltersSecondary>
              </div>
            ) : (
              <div className={css.listingsForMapVariant}>
                {searchListingsError ? (
                  <H3 className={css.error}>
                    <FormattedMessage id="SearchPage.searchError" />
                  </H3>
                ) : null}
                {!isValidDatesFilter ? (
                  <H5>
                    <FormattedMessage id="SearchPage.invalidDatesFilter" />
                  </H5>
                ) : null}
                {!(listingsAreLoaded || searchListingsError) ? (
                  <div className={css.listingsLoading} role="status" aria-live="polite">
                    <IconSpinner />
                    <p className={css.listingsLoadingText}>
                      <FormattedMessage id="MainPanelHeader.loadingResults" />
                    </p>
                  </div>
                ) : (
                  <>
                    <SearchResultsPanel
                      className={css.searchListingsPanel}
                      listings={listings}
                      pagination={pagination}
                      search={searchParamsInURL}
                      setActiveListing={onActivateListing}
                      isMapVariant
                      slugParam={currentPathParams?.slug}
                      isPromotionsPage={isPromotionsPage}
                    />
                    {faqItems.length > 0 ? (
                      <section className={css.faqSection}>
                        <h2 className={css.faqHeading}>FAQ</h2>
                        <div className={css.faqList}>
                          {faqItems.map((item, index) => (
                            <div key={`${item.question}-${index}`} className={css.faqItem}>
                              <h3 className={css.faqQuestion}>{item.question}</h3>
                              <p className={css.faqAnswer}>{item.answer}</p>
                            </div>
                          ))}
                        </div>
                      </section>
                    ) : null}
                  </>
                )}
              </div>
            )}
          </div>
          <ModalInMobile
            className={css.mapPanel}
            id="SearchPage_map"
            isModalOpenOnMobile={this.state.isSearchMapOpenOnMobile}
            onClose={() => this.setState({ isSearchMapOpenOnMobile: false })}
            showAsModalMaxWidth={MODAL_BREAKPOINT}
            onManageDisableScrolling={onManageDisableScrolling}
          >
            <div className={css.mapWrapper} data-testid="searchMapContainer">
              {shouldShowSearchMap ? (
                <SearchMap
                  reusableContainerClassName={css.map}
                  rootClassName={css.mapRoot}
                  activeListingId={activeListingId}
                  bounds={bounds}
                  center={origin}
                  isSearchMapOpenOnMobile={this.state.isSearchMapOpenOnMobile}
                  location={location}
                  listings={listings || []}
                  onMapMoveEnd={this.onMapMoveEnd}
                  onCloseAsModal={() => {
                    onManageDisableScrolling('SearchPage_map', false);
                  }}
                  messages={intl.messages}
                />
              ) : null}
            </div>
          </ModalInMobile>
        </div>
      </Page>
    );
  }
}

/**
 * SearchPage component with map layout
 *
 * @param {Object} props
 * @param {propTypes.uuid} [props.activeListingId] - The active listing ID
 * @param {propTypes.currentUser} [props.currentUser] - The current user
 * @param {Array<propTypes.listing>} [props.listings] - The listings
 * @param {propTypes.pagination} [props.pagination] - The pagination
 * @param {boolean} [props.scrollingDisabled] - Whether the scrolling is disabled
 * @param {boolean} [props.searchInProgress] - Whether the search is in progress
 * @param {propTypes.error} [props.searchListingsError] - The search listings error
 * @param {Object} [props.searchParams] - The search params from the Redux state
 * @param {Function} [props.onActivateListing] - The function to activate a listing
 * @param {Function} [props.onManageDisableScrolling] - The function to manage the disable scrolling
 * @returns {JSX.Element}
 */
const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const searchListingsError = props.searchListingsError;
  if (isForbiddenError(searchListingsError)) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const { currentUser, ...restOfProps } = props;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(searchListingsError);
  const hasNoViewingRightsError = isErrorNoViewingPermission(searchListingsError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (hasNoViewingRightsUser || hasNoViewingRightsError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <SearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      currentUser={currentUser}
      {...restOfProps}
    />
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
    activeListingId,
    filterOptionCounts,
  } = state.SearchPage;
  const listings = getListingsById(state, currentPageResultIds);

  return {
    currentUser,
    listings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
    activeListingId,
    filterOptionCounts,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onActivateListing: listingId => dispatch(setActiveListing(listingId)),
  onFetchFilterCounts: (searchParams, filters) =>
    dispatch(fetchFilterOptionCounts(searchParams, filters)),
  onLoadData: (params, search, config) => dispatch(loadData(params, search, config)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SearchPage = compose(connect(mapStateToProps, mapDispatchToProps))(EnhancedSearchPage);

export default SearchPage;
